import React, { useEffect, useState, useReducer } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetProjectDetailsQuery, useCreateProjectMutation, useGetUsersQuery } from '@/redux/slices/define/defineApiSlice';
import { setDefineData, addShippingMode, removeShippingMode, updateShippingMode, setTarget } from '@/redux/slices/define/defineSlice';
import { Label } from "@radix-ui/react-dropdown-menu";
import { Input } from "@/components/ui/input";
import { AlertTriangle, ChevronDown, Info, Pencil, Plus, Search, X } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import AlertTooltip from '../alert-tooltip';
import Footer from "@/components/footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Loading from '../Loading';
import { cn } from "@/lib/utils";
import { db } from '@/services/Database';
import { useGetPalletsQuery } from '@/redux/slices/pallet/palletApiSlice';
import { convertToPalletType } from '@/components/pallet-config/offline-pallet-config';
import { useGetProjectsQuery } from '@/redux/slices/define/defineApiSlice';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { transitModes } from '@/lib/common';
import { showErrorToast, showSuccessToast } from "@/components/toast/toast";
import { setProjectId } from '@/redux/slices/define/defineSlice';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from '@/hooks/useAuth';
import { useDeleteShippigModeMutation } from '@/redux/slices/define/defineApiSlice';
import { setPalletTypes } from '@/redux/slices/pallet/palletSlice';
import { Skeleton } from "@/components/ui/skeleton";
import axios from 'axios';
import { backendApiUrl } from '@/lib/config';
import oktaService from '@/services/OktaService';
import { Checkbox } from "@/components/ui/checkbox";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface ShippingMode {
  id?: number;
  mode: number;
  transitMode: string;
  startCity: string;
  startStateName: string;
  startZip: string;
  endCity: string;
  endStateName: string;
  endZip: string;
  distanceTravelled: string;
  elevationChanges: string;
  elevationMax: string;
  intermediateStopNotes?: string;
  isRoundTrip?: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
}

interface DefineFormValues {
  type: 'create' | 'update';
  projectId?: number;
  projectDetails: {
    projectName: string;
    projectDescription: string;
    id?: any;
  };
  shippingModes: ShippingMode[];
  users?: any[];
}

interface DefineProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => void;
}

export default function Define({ projectId, isOffline, onOfflineSubmit }: DefineProps) {
  const dispatch = useDispatch();
  const defineData = useSelector((state: RootState) => state.define);
  const { data: projectDetails, isLoading: isLoadingProjectDetails, refetch } = useGetProjectDetailsQuery(projectId || defineData.projectId, { 
    // skip: !projectId,
    refetchOnMountOrArgChange: true
  });
  const [deleteShippigMode, { isLoading: isDeletingShippingMode }] = useDeleteShippigModeMutation()
  const { data: pallets, isFetching, refetch: refetchPallets } = useGetPalletsQuery(projectId ?? '', {
    skip: !projectId || isOffline,
    refetchOnMountOrArgChange: true
  });
  const [isDataReady, setIsDataReady] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { user } = useAuth()
  const [createProject, { isLoading: isCreatingProject }] = useCreateProjectMutation();
  const [defaultTab, setDefaultTab] = useState<string | undefined>(undefined);
  const { data: allUsers, isLoading: isLoadingUsers } = useGetUsersQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<any>([]);
  const [tempSelectedUsers, setTempSelectedUsers] = useState<any>([]);
  const [initialFormValues, setInitialFormValues] = useState<DefineFormValues | null>(null);
  
  const validationSchema = Yup.object().shape({
    projectDetails: Yup.object().shape({
      projectName: Yup.string().required('Project Name is required'),
      projectDescription: Yup.string().required('Project Description is required'),
    }),
    shippingModes: Yup.array().of(
      Yup.object().shape({
        mode: Yup.number().required('Mode is required'),
        transitMode: Yup.string().required('Transit Mode is required'),
        startCity: Yup.string().required('Start City is required'),
        startStateName: Yup.string().required('Start State Name is required'),
        startZip: Yup.string().required('Start Zip is required'),
        endCity: Yup.string().required('End City is required'),
        endStateName: Yup.string().required('End State Name is required'),
        endZip: Yup.string().required('End Zip is required'),
        distanceTravelled: Yup.string().required('Distance Travelled is required'),
        elevationChanges: Yup.string().required('Elevation Changes is required'),
        elevationMax: Yup.string().when('elevationChanges', {
          is: (value: any) => value === 'yes',
          then: (schema) => schema.required('Elevation Max is required when Elevation Changes is Yes'),
          otherwise: (schema) => schema.notRequired(),
        }),
        intermediateStopNotes: Yup.string().when('mode', {
          is: (mode: number) => mode > 1,
          then: () => Yup.string(),
          otherwise: () => Yup.string(),
        }),
      })
    ),
    users: Yup.array().of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        email: Yup.string().email().required(),
      })
    ),
  });

  const formik = useFormik<DefineFormValues>({
    initialValues: {
      type: projectId ? 'update' : 'create',
      projectId: projectId ? parseInt(projectId) : undefined,
      projectDetails: {
        projectName: '',
        projectDescription: '',
      },
      shippingModes: [
        {
          mode: 1,
          transitMode: "",
          startCity: "",
          startStateName: "",
          startZip: "",
          endCity: "",
          endStateName: "",
          endZip: "",
          distanceTravelled: "",
          elevationChanges: "",
          elevationMax: "",
          intermediateStopNotes: "",
        }
      ],
      users: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      if (isOffline && onOfflineSubmit) {
        onOfflineSubmit(values);
        showSuccessToast("Define Stage Saved Offline");
        return;
      }

      try {
        let result = await createProject({
          ...values,
          type: values.projectId ? "update" : 'create',
          projectId: values.projectId ? values.projectId : values.projectDetails?.id,
          users: selectedUsers.map((user: any) => user.id)
        }).unwrap();
        formik.setFieldValue('type', 'update');
        formik.setFieldValue('projectId', result.project.id);
        dispatch(setDefineData({
          ...result,
          projectDetails: result.project,
          projectId: result.project.id,
          target: result.project.target || '',
          plusByUser: result.project.plusByUser || '',
          percentage: result.project.percentage || '',
          users: result.project.users || []
        }));
        setInitialFormValues({
          ...values,
          type: 'update',
          projectId: result.project.id,
          projectDetails: result.project,
          users: result.project.users || []
        });
        setHasUnsavedChanges(false);
        console.log({
          ...values,
          type: values.projectId ? "update" : 'create',
          projectId: values.projectId ? values.projectId : values.projectDetails?.id,
          users: selectedUsers.map((user: any) => user.id)
        }, 'valll')
        console.log("{values of define", {
          ...values,
          type: values.projectId ? "update" : 'create',
          projectId: values.projectId ? values.projectId : values.projectDetails?.id,
          users: selectedUsers.map((user: any) => user.id)
        })
        showSuccessToast("Define Stage Saved");
      } catch (error: any) {
        console.error(error, 'errruuurr');
        if (error?.status === 403) {
          showErrorToast("You are not authorized to edit this project")
        } else {
          showErrorToast("Failed To Complete Define Stage");
        }
      }
    },
    enableReinitialize: true,
  });



  useEffect(() => {
    const loadPalletData = async () => {
      try {
        if (projectId && isOffline) {
          try {
            // setIsLoading(true);
            const response = await db.getAllPalletsForProject(parseInt(projectId));
            console.log(response, 'response')
            // Even if no pallets found, we should still initialize with empty array
            const pallets = response?.pallets || [];
            const convertedPallets = pallets.map(convertToPalletType);
            dispatch(setPalletTypes(convertedPallets));

          } catch (error) {
            console.error('Error loading pallet data:', error);
            showErrorToast('Failed to load pallet configuration');
            dispatch(setPalletTypes([]));
          }
        }
      } catch (error) {
        console.error('Error loading pallet data:', error);
      }
    };

    loadPalletData();
  }, [projectId, isOffline]);
  useEffect(() => {
    if (projectDetails) {
      const formattedData: DefineFormValues = {
        type: 'update',
        projectId: projectDetails.id,
        projectDetails: {
          projectName: projectDetails.projectName || '',
          projectDescription: projectDetails.projectDescription || '',
        },
        shippingModes: projectDetails.shippingModes.map((mode: any) => ({
          id: mode.id,
          mode: mode.mode,
          transitMode: mode.transitMode || '',
          startCity: mode.startCity || '',
          startStateName: mode.startStateName || '',
          startZip: mode.startZip || '',
          endCity: mode.endCity || '',
          endStateName: mode.endStateName || '',
          endZip: mode.endZip || '',
          distanceTravelled: mode.distanceTravelled?.toString() || '',
          elevationChanges: mode.elevationChanges || '',
          elevationMax: mode.elevationMax || '',
          intermediateStopNotes: mode.intermediateStopNotes || '',
        })),
        users: projectDetails.users || [],
      };
      setSelectedUsers(projectDetails.users?.length > 0 ? [projectDetails?.creator, ...projectDetails?.users] : [projectDetails?.creator]);
      setInitialFormValues(formattedData);
      dispatch(setDefineData({
        ...formattedData,
        target: projectDetails.target || '',
        plusByUser: projectDetails.plusByUser || '',
        percentage: projectDetails.percentage || '',
        users: projectDetails.users || [],
      }));
    }
  }, [projectDetails, dispatch]);

  useEffect(() => {
    if (defineData) {
      const safeDefineData = {
        ...defineData,
        projectDetails: {
          projectName: defineData.projectDetails?.projectName || '',
          projectDescription: defineData.projectDetails?.projectDescription || '',
        },
        shippingModes: defineData.shippingModes?.map((mode: any) => ({
          ...mode,
          transitMode: mode.transitMode || '',
          startCity: mode.startCity || '',
          startStateName: mode.startStateName || '',
          startZip: mode.startZip || '',
          endCity: mode.endCity || '',
          endStateName: mode.endStateName || '',
          endZip: mode.endZip || '',
          distanceTravelled: mode.distanceTravelled?.toString() || '',
          elevationChanges: mode.elevationChanges || '',
          elevationMax: mode.elevationMax || '',
          intermediateStopNotes: mode.intermediateStopNotes || '',
        })) || [],
        users: defineData.users || [],
      };
      formik.setValues(safeDefineData);
      forceUpdate();
    }
  }, [defineData, formik.setValues]);

  useEffect(() => {
    if (initialFormValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialFormValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formik.values, initialFormValues]);

  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const handleAddShippingMode = () => {
    const newMode: ShippingMode = {
      mode: formik.values.shippingModes.length + 1,
      transitMode: "",
      startCity: "",
      startStateName: "",
      startZip: "",
      endCity: "",
      endStateName: "",
      endZip: "",
      distanceTravelled: "",
      elevationChanges: "",
      elevationMax: "",
      intermediateStopNotes: "",
    };
    const updatedShippingModes = [...formik.values.shippingModes, newMode];
    dispatch(setDefineData({ ...formik.values, shippingModes: updatedShippingModes }));
  };

  const handleRemoveShippingMode = (index: number) => {
    const updatedShippingModes = formik.values.shippingModes.filter((_, i) => i !== index);
    dispatch(setDefineData({ ...formik.values, shippingModes: updatedShippingModes }));
  };

  const handleDuplicateShippingMode = (index: number) => {
    const modeToDuplicate = { ...formik.values.shippingModes[index], mode: formik.values.shippingModes.length + 1 };
    delete modeToDuplicate.id;
    const updatedShippingModes = [...formik.values.shippingModes, modeToDuplicate];
    dispatch(setDefineData({ ...formik.values, shippingModes: updatedShippingModes }));
  };

  // Add new function to create round trip shipping mode
  const handleCreateRoundTripMode = (index: number) => {
    const sourceMode = formik.values.shippingModes[index];
    const newMode = {
      // ...sourceMode,
      id: undefined,
      mode: formik.values.shippingModes.length + 1,
      isRoundTrip: true,
      startCity: sourceMode.endCity,
      startStateName: sourceMode.endStateName,
      startZip: sourceMode.endZip,
      endCity: sourceMode.startCity,
      endStateName: sourceMode.startStateName,
      endZip: sourceMode.startZip
    };

    const updatedShippingModes = [...formik.values.shippingModes, newMode];
    dispatch(setDefineData({ ...formik.values, shippingModes: updatedShippingModes }));
    showSuccessToast("Round Trip Shipping Mode created");
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (isUserDialogOpen) {
      setTempSelectedUsers([...selectedUsers]);
    }
  }, [isUserDialogOpen, selectedUsers]);

  const handleAddUserToProject = () => {
    setSelectedUsers([...tempSelectedUsers]);
    setSearchTerm('');
    setIsUserDialogOpen(false);
  };

  const handleRemoveUser = (userId: number) => {
    const updatedUsers = selectedUsers.filter((user: any) => user.id !== userId);
    setSelectedUsers(updatedUsers);
  };

  const handleDeleteShippingMode = async (index: number) => {
    const shippingMode = formik.values.shippingModes[index];

    if (!shippingMode.id) {
      const updatedShippingModes = formik.values.shippingModes.filter((_, i) => i !== index);
      dispatch(setDefineData({ ...formik.values, shippingModes: updatedShippingModes }));
      return;
    }

    try {
      const response = await deleteShippigMode({ shippingModeId: shippingMode.id }).unwrap()
      const updatedShippingModes = formik.values.shippingModes.filter((_, i) => i !== index);
      dispatch(setDefineData({ ...formik.values, shippingModes: updatedShippingModes }));
      showSuccessToast('Shipping mode deleted successfully');
    } catch (error: any) {
      console.log(error)
      if (error?.status === 400 && error?.data?.inUse) {
        showErrorToast(error?.data?.message)
      } else {
        console.error('Error deleting shipping mode:', error);
        showErrorToast('Failed to delete shipping mode');
      }
    }
  };
  const handleFormChange = () => {
    setHasUnsavedChanges(true);
  };
  console.log(selectedUsers, 'selectedUsers')
  // if (isLoadingProjectDetails) return <div className="">Loading...</div>
  console.log(projectDetails, 'projectDetailsss', allUsers, 'allUsers', selectedUsers, 'selectedUsers')

  // Handle round trip checkbox change
  const handleRoundTripChange = (index: number, checked: boolean) => {
    const updatedShippingModes = [...formik.values.shippingModes];
    updatedShippingModes[index] = {
      ...updatedShippingModes[index],
      isRoundTrip: checked
    };

    // If checked, copy start location to end location
    if (checked) {
      updatedShippingModes[index].endCity = updatedShippingModes[index].startCity;
      updatedShippingModes[index].endStateName = updatedShippingModes[index].startStateName;
      updatedShippingModes[index].endZip = updatedShippingModes[index].startZip;
    }

    dispatch(setDefineData({ ...formik.values, shippingModes: updatedShippingModes }));
  };

  // Inside the Define component, add this new function to handle start location field changes
  const handleStartLocationChange = (index: number, field: string, value: string) => {
    // Update the formik value for the start location field
    formik.setFieldValue(`shippingModes.${index}.${field}`, value);

    // If round trip is enabled, also update the corresponding end location field
    if (formik.values.shippingModes[index].isRoundTrip) {
      const endField = field.replace('start', 'end');
      formik.setFieldValue(`shippingModes.${index}.${endField}`, value);
    }
  };

  return (
    <form onChange={handleFormChange} onSubmit={formik.handleSubmit} className="w-full py-3 pb-[110px] h-[calc(100vh-140px)] max-h-auto overflow-y-scroll x-space">
      {isLoadingProjectDetails ? (
        <div className="w-full">
          {/* Project Details Skeleton */}
          <div className="flex-center justify-between my-5">
            <Skeleton className="h-7 w-36" />
            <Skeleton className="h-10 w-48" />
          </div>

          {/* Project Name & Description Skeleton */}
          <div className="flex-center flex-col gap-2 w-full">
            <div className="w-full border border-border-style-1 rounded-l-md flex-center">
              <div className="max-w-[180px] label-bg rounded-l-md">
                <Skeleton className="h-6 w-32 mx-4 my-3" />
              </div>
              <div className="relative w-full">
                <Skeleton className="h-10 w-full rounded-none" />
              </div>
            </div>

            <div className="w-full border rounded-r-md flex">
              <div className="max-w-[180px] label-bg">
                <Skeleton className="h-6 w-32 mx-4 my-3" />
              </div>
              <div className="relative w-full">
                <Skeleton className="h-20 w-full rounded-none" />
              </div>
            </div>
          </div>

          {/* Project Users Skeleton */}
          {!isOffline && (
            <div className="w-full my-5">
              <Skeleton className="h-7 w-36 mb-5" />
              <Skeleton className="h-10 w-48 mb-3" />

              <div className="border rounded-md p-4">
                <Skeleton className="h-6 w-48 mb-4" />
                <div className="flex flex-wrap gap-2">
                  <Skeleton className="h-10 w-32 rounded-full" />
                  <Skeleton className="h-10 w-36 rounded-full" />
                  <Skeleton className="h-10 w-28 rounded-full" />
                </div>
              </div>
            </div>
          )}

          {/* Shipping Modes Skeleton */}
          <div className="w-full">
            <Skeleton className="h-7 w-36 mt-8 mb-5" />
            <div className="flex gap-3 mb-4">
              <Skeleton className="h-8 w-24 rounded-lg" />
              <Skeleton className="h-8 w-24 rounded-lg" />
              <Skeleton className="h-8 w-24 rounded-lg" />
            </div>

            <div className="w-full my-5">
              <div className="flex-center border-border-style-1 border w-full">
                {Array(4).fill(0).map((_, index) => (
                  <div key={index} className="label-bg">
                    <Skeleton className="h-6 w-32 mx-4 my-3" />
                  </div>
                ))}
              </div>
              <div className="flex-center border-border-style-1 border w-full">
                {Array(4).fill(0).map((_, index) => (
                  <div key={index} className="relative w-full" style={{ height: "42px" }}>
                    <Skeleton className="h-full w-full rounded-none" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="w-full">
            <div className="flex-center justify-between">
              <h1 className="semi-bold-20 my-5">Project Details</h1>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="bg-blue hover:bg-[#00336C] text-white hover:text-white">
                    <span><Info size={20} /></span> Documents Needed
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">

                  <DialogHeader>
                    <DialogTitle className='semi-bold-20'><h1>Required Documents</h1></DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col gap-4 py-4">
                    <div className="flex items-start gap-2">
                      <span className=" font-medium">•</span>
                      <p className="">Primary pkg drawing/spec for each variable</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className=" ">•</span>
                      <p className="">Secondary/tertiary pkg drawing/spec for each variable</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className=" font-medium">•</span>
                      <p className="">Unit load diagram (tops/cape) for each variable</p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="flex-center flex-col gap-2 w-full">
              <div className="w-full border border-border-style-1 rounded-l-md flex-center">
                <div className="max-w-[180px] label-bg rounded-l-md">
                  <Label className="label">Project Name</Label>
                </div>
                <div className="relative w-full">
                  <Input
                    placeholder="Enter the Project Name"
                    {...formik.getFieldProps('projectDetails.projectName')}
                    className={cn(
                      "border-0",
                      formik.touched.projectDetails?.projectName && formik.errors.projectDetails?.projectName &&
                      "border-2 border-states-error outline-none"
                    )}
                  />
                  {formik.touched.projectDetails?.projectName && formik.errors.projectDetails?.projectName && (
                    <AlertTooltip message={formik.errors.projectDetails.projectName} />
                  )}
                </div>
              </div>

              <div className="w-full border rounded-r-md flex  ">
                <div className="max-w-[180px] label-bg">
                  <Label className="label">Project Description</Label>
                </div>
                <div className="relative w-full">
                  <Textarea
                    placeholder="Enter the Project Description"
                    {...formik.getFieldProps('projectDetails.projectDescription')}
                    className={cn(
                      "border-2 border-gray-200",
                      formik.touched.projectDetails?.projectDescription && formik.errors.projectDetails?.projectDescription &&
                      "border-2 border-states-error outline-none"
                    )}
                  />
                  {formik.touched.projectDetails?.projectDescription && formik.errors.projectDetails?.projectDescription && (
                    <AlertTooltip message={formik.errors.projectDetails.projectDescription} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {!isOffline && <div className="w-full my-5">
            <h1 className="semi-bold-20 mb-5">Project Users</h1>
            <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
              <DialogTrigger asChild>
                <Button className=" bg-blue hover:bg-[#00336C]  mb-3">Add Users to Project</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Users to Project</DialogTitle>
                </DialogHeader>
                <div className="flex items-center space-x-2 mb-4">
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button className=' bg-blue' onClick={() => setSearchTerm('')}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {allUsers?.filter((u: any) => (
                    // Filter out the creator and current user
                    (u?.id !== projectDetails?.creator?.id && u?.id !== user?.id) &&
                    // Add search term filter condition
                    (searchTerm === '' ||
                      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      u.email.toLowerCase().includes(searchTerm.toLowerCase()))
                  )).map((user: any) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-100 rounded-lg transition-colors duration-200 border-b last:border-b-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue/10 flex items-center justify-center">
                          <span className="text-blue font-medium">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.email} {`(${user.role})`}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={tempSelectedUsers?.some((u: any) => u.id === user.id)}
                          onChange={() => {
                            const updatedUsers = tempSelectedUsers.some((u: any) => u.id === user.id)
                              ? tempSelectedUsers.filter((u: any) => u.id !== user.id)
                              : [...tempSelectedUsers, user];
                            setTempSelectedUsers(updatedUsers);
                          }}
                          className="w-5 h-5 rounded border-gray-300 bg-blue checked:bg-blue text-blue focus:ring-blue cursor-pointer"
                          aria-label={`Select ${user.name}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setIsUserDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button className="bg-blue hover:bg-[#00336C]" onClick={handleAddUserToProject}>
                    Confirm
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="border rounded-md p-4">
              <h2 className="font-semibold mb-2">Current Project Users</h2>
              {selectedUsers?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {[...new Map(selectedUsers.map((u: any) => [u.id, u])).values()].map((u: any, i: number) => {
                    // Check if the user is an admin (case insensitive)
                    const isAdmin = u.role && typeof u.role === 'string' && u.role.toLowerCase().includes('admin');
                    // Check if user is owner
                    const isOwner = projectDetails?.creator?.id === u?.id;

                    return (
                      <div key={i} className={`inline-flex items-center gap-2 px-4 py-[9px] h-10 rounded-[14px] ${isOwner ? "bg-blue" : "bg-white border border-blue"}`}>
                        <div className="flex items-center gap-3">
                          <span className={`  font-semibold ${isOwner ? "text-white" : "text-blue"}`}>
                            {u?.name || "User"}
                          </span>

                          {/* Role badges */}
                          {isOwner && (
                            <span className="text-xs  border border-white py-[4px] bg-blue text-white px-2 rounded-full">Owner</span>
                          )}

                          {(isOwner && isAdmin) && (
                            <span className="text-xs bg-blue border py-[4px] border-white text-white px-2  rounded-full">{u?.role}</span>
                          )}
                          {(!isOwner) && (
                            <span className="text-xs text-blue border py-[4px] bg-[#EDF5FF] border-white px-2  rounded-full">{u?.role}</span>
                          )}

                          {/* Delete button shown only to owner */}
                          {!isOwner && projectDetails?.creator?.id === user?.id && (
                            <X
                              size={14}
                              className="cursor-pointer hover:text-states-error transition-colors"
                              onClick={() => handleRemoveUser(u.id)}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>No users added to this project yet.</p>
              )}
            </div>
          </div>
          }
          <div className="w-full">
            <h1 className="semi-bold-20 mt-8 mb-5">Shipping Modes</h1>
            {formik.values.shippingModes?.length > 0 && (
              <Tabs defaultValue={formik.values.shippingModes[0].mode.toString()}>
                <TabsList className="gap-3 bg-white">
                  {formik.values.shippingModes.map((mode, index) => (
                    <TabsTrigger
                      key={index}
                      value={mode.mode.toString()}
                      className="justify-start flex-center items-center gap-2 w-fit px-3 text-left data-[state=active]:bg-blue data-[state=active]:text-white data-[state=inactive]:bg-states-disabled/50 py-2 rounded-lg text-sm capitalize"
                    >
                      <span>{`Mode ${mode.mode}`}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild className="bg-transparent text-white p-0 rounded-lg font-semibold text-sm flex capitalize items-center gap-2 outline-none">
                          <span><ChevronDown size={18} /></span>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="rounded-lg p-0" forceMount>
                          <DropdownMenuItem className="p-1" onClick={() => handleCreateRoundTripMode(index)}>
                            Round Trip Shipment
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-border-style-1" />
                          <DropdownMenuItem className="p-1" onClick={() => handleDuplicateShippingMode(index)}>
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-border-style-1" />
                          <DropdownMenuItem
                            onClick={() => handleDeleteShippingMode(index)}
                            className="text-states-error p-1"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TabsTrigger>
                  ))}
                  <Button
                    type="button"
                    onClick={handleAddShippingMode}
                    className="border-2 border-blue bg-white text-blue hover:bg-blue hover:text-white flex-center px-4 h-8 rounded-md semi-bold-16 text-sm"
                  >
                    <span><Plus size={20} /></span>
                    Add
                  </Button>
                </TabsList>
                {formik.values.shippingModes.map((mode, index) => (
                  <TabsContent key={index} value={mode.mode.toString()}>
                    <div className="w-full my-5">
                      {/* Round Trip Checkbox */}
                      {/* <div className="flex items-center space-x-2 mb-4">
                        <Checkbox
                          id={`round-trip-${index}`}
                          checked={mode.isRoundTrip}
                          onCheckedChange={(checked) => handleRoundTripChange(index, !!checked)}
                        />
                        <label
                          htmlFor={`round-trip-${index}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Round Trip Shipment (copies start location to end location)
                        </label>
                      </div> */}

                      <div className="flex-center border-border-style-1 border w-full">
                        <div className="label-bg">
                          <Label className="label">Transit Mode</Label>
                        </div>
                        <div className="label-bg">
                          <Label className="label">Start City</Label>
                        </div>
                        <div className="label-bg">
                          <Label className="label">Start State Name</Label>
                        </div>
                        <div className="label-bg">
                          <Label className="label">Start Zip</Label>
                        </div>
                      </div>
                      <div className="flex-center border-border-style-1 border w-full">
                        <div className="relative w-full">
                          <Select
                            value={formik.values.shippingModes[index].transitMode}
                            onValueChange={(value) => {
                              console.log(value, 'vallue is being changed')
                              if (value !== '') {
                                formik.setFieldValue(`shippingModes.${index}.transitMode`, value)
                              }
                            }}
                          >
                            <SelectTrigger className={cn(
                              "w-full rounded-none bg-transparent border",
                              formik.touched.shippingModes?.[index]?.transitMode &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'transitMode' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}>
                              <SelectValue placeholder="Select Transit Mode" />
                            </SelectTrigger>
                            <SelectContent>
                              {transitModes.map((item, i) => (
                                <SelectItem key={i} value={item?.toString()}>
                                  {item}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {formik.touched.shippingModes?.[index]?.transitMode &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'transitMode' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].transitMode as string} />
                            )}
                        </div>
                        <div className="relative w-full">
                          <Input
                            placeholder="Enter the Start City"
                            value={formik.values.shippingModes[index].startCity}
                            onChange={(e) => handleStartLocationChange(index, 'startCity', e.target.value)}
                            className={cn(
                              formik.touched.shippingModes?.[index]?.startCity &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'startCity' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}
                          />
                          {formik.touched.shippingModes?.[index]?.startCity &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'startCity' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].startCity as string} />
                            )}
                        </div>
                        <div className="relative w-full">
                          <Input
                            placeholder="Enter the Start State Name"
                            value={formik.values.shippingModes[index].startStateName}
                            onChange={(e) => handleStartLocationChange(index, 'startStateName', e.target.value)}
                            className={cn(
                              formik.touched.shippingModes?.[index]?.startStateName &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'startStateName' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}
                          />
                          {formik.touched.shippingModes?.[index]?.startStateName &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'startStateName' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].startStateName as string} />
                            )}
                        </div>
                        <div className="relative w-full">
                          <Input
                            type="number"
                            placeholder="Enter the Start Zip"
                            value={formik.values.shippingModes[index].startZip}
                            onChange={(e) => handleStartLocationChange(index, 'startZip', e.target.value)}
                            className={cn(
                              formik.touched.shippingModes?.[index]?.startZip &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'startZip' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}
                          />
                          {formik.touched.shippingModes?.[index]?.startZip &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'startZip' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].startZip as string} />
                            )}
                        </div>
                      </div>
                      <div className="flex-center border-border-style-1 border w-full">
                        <div className="label-bg">
                          <Label className="label">Distance Travelled (Miles)</Label>
                        </div>
                        <div className="label-bg">
                          <Label className="label">End City</Label>
                        </div>
                        <div className="label-bg">
                          <Label className="label">End State Name</Label>
                        </div>
                        <div className="label-bg">
                          <Label className="label">End Zip</Label>
                        </div>

                      </div>
                      <div className="flex-center border-border-style-1 border w-full">
                        <div className="relative w-full">
                          <Input
                            placeholder="Enter the Distance Travelled"
                            type="number"
                            {...formik.getFieldProps(`shippingModes.${index}.distanceTravelled`)}
                            className={cn(
                              formik.touched.shippingModes?.[index]?.distanceTravelled &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'distanceTravelled' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}
                          />
                          {formik.touched.shippingModes?.[index]?.distanceTravelled &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'distanceTravelled' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].distanceTravelled as string} />
                            )}
                        </div>
                        <div className="relative w-full">
                          <Input
                            placeholder="Enter the End City"
                            {...formik.getFieldProps(`shippingModes.${index}.endCity`)}
                            className={cn(
                              formik.touched.shippingModes?.[index]?.endCity &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'endCity' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}
                          />
                          {formik.touched.shippingModes?.[index]?.endCity &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'endCity' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].endCity as string} />
                            )}
                        </div>
                        <div className="relative w-full">
                          <Input
                            placeholder="Enter the End State Name"
                            {...formik.getFieldProps(`shippingModes.${index}.endStateName`)}
                            className={cn(
                              formik.touched.shippingModes?.[index]?.endStateName &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'endStateName' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}
                          />
                          {formik.touched.shippingModes?.[index]?.endStateName &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'endStateName' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].endStateName as string} />
                            )}
                        </div>

                        <div className="relative w-full">
                          <Input
                            type='number'
                            placeholder="Enter the End Zip"
                            {...formik.getFieldProps(`shippingModes.${index}.endZip`)}
                            className={cn(
                              formik.touched.shippingModes?.[index]?.endZip &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'endZip' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}
                          />
                          {formik.touched.shippingModes?.[index]?.endZip &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'endZip' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].endZip as string} />
                            )}
                        </div>

                      </div>
                      <div className="flex-center border-border-style-1 border w-full mt-5">
                        <div className="label-bg">
                          <Label className="label">Elevation Changes</Label>
                        </div>
                        <div className="label-bg">
                          <Label className="label">Elevation Max (Ft)</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="ml-2 text-gray-500 cursor-help">
                                  <Info size={16} />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>To get elevation max data, use Google Maps cycling directions between your start and end points. The elevation data is shown in the route details.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                      <div className="flex-center border-border-style-1 border w-full">
                        <div className="relative w-full">
                          <Select

                            value={formik.values.shippingModes[index].elevationChanges}
                            onValueChange={(value) => {
                              console.log(value === '', 'vallue is being changed')

                              if (value !== '') {
                                formik.setFieldValue(`shippingModes.${index}.elevationChanges`, value)
                              }
                            }}
                          >
                            <SelectTrigger className={cn(
                              "w-full rounded-none bg-transparent border",
                              formik.touched.shippingModes?.[index]?.elevationChanges &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'elevationChanges' in formik.errors.shippingModes[index] &&
                              "border-2 border-states-error outline-none"
                            )}>
                              <SelectValue placeholder="Select Elevation Changes" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                          {formik.touched.shippingModes?.[index]?.elevationChanges &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'elevationChanges' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].elevationChanges as string} />
                            )}
                        </div>
                        <div className="relative w-full">
                          <div className="flex items-center">

                            <Input
                              placeholder="Enter the Elevation Max"
                              type="number"
                              {...formik.getFieldProps(`shippingModes.${index}.elevationMax`)}
                              disabled={formik.values.shippingModes[index].elevationChanges !== "yes"}
                              className={cn(
                                formik.touched.shippingModes?.[index]?.elevationMax &&
                                formik.errors.shippingModes?.[index] &&
                                typeof formik.errors.shippingModes[index] !== 'string' &&
                                'elevationMax' in formik.errors.shippingModes[index] &&
                                "border-2 border-states-error outline-none"
                              )}
                            />

                          </div>
                          {formik.touched.shippingModes?.[index]?.elevationMax &&
                            formik.errors.shippingModes?.[index] &&
                            typeof formik.errors.shippingModes[index] !== 'string' &&
                            'elevationMax' in formik.errors.shippingModes[index] && (
                              <AlertTooltip message={formik.errors.shippingModes[index].elevationMax as string} />
                            )}
                        </div>
                      </div>
                      {mode.mode > 1 && (
                        <div className="w-full border border-border-style-1 rounded-l-md flex-center mt-5">
                          <div className="max-w-[300px] label-bg rounded-l-md">
                            <Label className="label">Intermediate Stop Notes</Label>
                          </div>
                          <div className="relative w-full">
                            <Input
                              placeholder="Enter the Intermediate Stop Notes"
                              {...formik.getFieldProps(`shippingModes.${index}.intermediateStopNotes`)}
                              className={cn(
                                formik.touched.shippingModes?.[index]?.intermediateStopNotes &&
                                formik.errors.shippingModes?.[index] &&
                                typeof formik.errors.shippingModes[index] !== 'string' &&
                                'intermediateStopNotes' in formik.errors.shippingModes[index] &&
                                "border-2 border-states-error outline-none"
                              )}
                            />
                            {formik.touched.shippingModes?.[index]?.intermediateStopNotes &&
                              formik.errors.shippingModes?.[index] &&
                              typeof formik.errors.shippingModes[index] !== 'string' &&
                              'intermediateStopNotes' in formik.errors.shippingModes[index] && (
                                <AlertTooltip message={formik.errors.shippingModes[index].intermediateStopNotes as string} />
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
          {!isOffline && <Footer

            hasUnsavedChanges={hasUnsavedChanges}
            hasErrors={!formik.isValid}
            isLoading={isCreatingProject}
            onSubmit={formik.handleSubmit}
          />}
        </>
      )}
    </form>
  );
}
